import { Box, Button, Menu, MenuItem, FormControl, InputLabel, Select, styled, CircularProgress } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import React, { useState, useEffect } from 'react';
import useFunctionOptions from '../utils/useFunctionOptions';

const StyledMenuItem = styled(MenuItem)({
    color: 'black',
    '&:hover': {
        backgroundColor: '#f0f0f0',
    },
});

interface FilterProps {
    levels: number;
    initialValues?: {
        l1?: string;
        l2?: string;
        l3?: string;
        l4?: string;
    };
    onApplyFilter: (function_l1: string, function_l2: string, function_l3: string, function_l4: string) => void;
}

const Filter: React.FC<FilterProps> = ({ levels, initialValues, onApplyFilter }) => {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [functionL1, setFunctionL1] = useState(initialValues?.l1 || '');
    const [functionL2, setFunctionL2] = useState(initialValues?.l2 || '');
    const [functionL3, setFunctionL3] = useState(initialValues?.l3 || '');
    const [functionL4, setFunctionL4] = useState(initialValues?.l4 || '');

    // Sync with initialValues when they change
    useEffect(() => {
        setFunctionL1(initialValues?.l1 || '');
        setFunctionL2(initialValues?.l2 || '');
        setFunctionL3(initialValues?.l3 || '');
        setFunctionL4(initialValues?.l4 || '');
    }, [initialValues]);

    // Use the custom hook to fetch options for each level
    const { options: l1Options, loading: l1Loading } = useFunctionOptions('l1', '');
    const { options: l2Options, loading: l2Loading } = useFunctionOptions('l2', functionL1);
    const { options: l3Options, loading: l3Loading } = useFunctionOptions('l3', functionL2);
    const { options: l4Options, loading: l4Loading } = useFunctionOptions('l4', functionL3);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleApplyFilter = () => {
        onApplyFilter(functionL1, functionL2, functionL3, functionL4);
        handleClose();
    };

    const handleClearFilter = () => {
        setFunctionL1('');
        setFunctionL2('');
        setFunctionL3('');
        setFunctionL4('');
        onApplyFilter('', '', '', '');
    };

    const renderFunctionLevel = (level: number) => {
        const levelProps = {
            level,
            functionL1,
            functionL2,
            functionL3,
            functionL4,
            setFunctionL1,
            setFunctionL2,
            setFunctionL3,
            setFunctionL4,
            options: level === 1 ? l1Options : 
                    level === 2 ? l2Options : 
                    level === 3 ? l3Options : l4Options,
            loading: level === 1 ? l1Loading : 
                    level === 2 ? l2Loading : 
                    level === 3 ? l3Loading : l4Loading,
        };

        return <FunctionLevelSelect key={`level-${level}`} {...levelProps} />;
    };

    

    return (
        <>
            <Button
                variant="outlined"
                endIcon={anchorEl ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                onClick={handleClick}
                sx={{
                    color: '#000',
                    fontFamily: 'Avantt',
                    textTransform: 'none',
                    borderColor: '#000',
                    padding: '10px 32px',
                    fontWeight: 'bold',
                }}
            >
                Filter
                {(functionL1 || functionL2 || functionL3 || functionL4) && ' (Applied)'}
            </Button>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                PaperProps={{
                    sx: {
                        width: 320,
                        padding: 2,
                        borderRadius: '11px',
                    },
                }}
            >
                <Box sx={{ mt: 2, width: 280 }}>
                    <Box sx={{ display: 'grid', gap: 2 }}>
                        {Array.from({ length: levels }, (_, index) => renderFunctionLevel(index + 1))}
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: 2 }}>
                    <Button
                        onClick={handleClearFilter}
                        sx={{
                            fontFamily: 'Avantt',
                            textTransform: 'none',
                            fontWeight: 'bold',
                            color: '#000',
                            backgroundColor: '#f0f0f0',
                            width: '50%',
                            '&:hover': {
                                backgroundColor: '#e0e0e0',
                            },
                        }}
                    >
                        Clear
                    </Button>
                    <Button
                        onClick={handleApplyFilter}
                        sx={{
                            fontFamily: 'Avantt',
                            textTransform: 'none',
                            fontWeight: 'bold',
                            color: '#fff',
                            backgroundColor: '#000',
                            width: '50%',
                            '&:hover': {
                                backgroundColor: '#333',
                            },
                        }}
                    >
                        Apply
                    </Button>
                </Box>
            </Menu>
        </>
    );
};

// Separate component for better readability and state management
const FunctionLevelSelect = ({
    level,
    functionL1,
    functionL2,
    functionL3,
    functionL4,
    setFunctionL1,
    setFunctionL2,
    setFunctionL3,
    setFunctionL4,
    options,
    loading,
}: {
    level: number;
    functionL1: string;
    functionL2: string;
    functionL3: string;
    functionL4: string;
    setFunctionL1: (value: string) => void;
    setFunctionL2: (value: string) => void;
    setFunctionL3: (value: string) => void;
    setFunctionL4: (value: string) => void;
    options: any[];
    loading: boolean;
}) => {
    const getCurrentValue = () => {
        switch (level) {
            case 1: return functionL1;
            case 2: return functionL2;
            case 3: return functionL3;
            case 4: return functionL4;
            default: return '';
        }
    };

    const handleChange = (value: string) => {
        switch (level) {
            case 1:
                setFunctionL1(value);
                setFunctionL2('');
                setFunctionL3('');
                setFunctionL4('');
                break;
            case 2:
                setFunctionL2(value);
                setFunctionL3('');
                setFunctionL4('');
                break;
            case 3:
                setFunctionL3(value);
                setFunctionL4('');
                break;
            case 4:
                setFunctionL4(value);
                break;
        }
    };

    const isDisabled = () => {
        switch (level) {
            case 2: return !functionL1;
            case 3: return !functionL2;
            case 4: return !functionL3;
            default: return false;
        }
    };

    // Convert options to strings and ensure uniqueness
    const stringOptions = Array.from(
        new Set(options.map(option => {
            if (typeof option === 'string') return option;
            if (option?.value) return option.value;
            if (option?.label) return option.label;
            return JSON.stringify(option);
        }))
    );

    return (
        <FormControl fullWidth>
            <InputLabel>{`Function L${level} *`}</InputLabel>
            <Select
                sx={{ borderRadius: '8px', color: '#000' }}
                value={getCurrentValue()}
                label={`Function L${level} *`}
                onChange={(e) => handleChange(e.target.value as string)}
                disabled={isDisabled()}
                renderValue={(selected) => selected || `Select Function L${level}`}
            >
                <StyledMenuItem value="">
                    <em>Select Function L{level}</em>
                </StyledMenuItem>
                {loading ? (
                    <StyledMenuItem value="">
                        <CircularProgress size={24} />
                    </StyledMenuItem>
                ) : (
                    stringOptions.map((option, index) => (
                        <StyledMenuItem key={index} value={option}>
                            {option}
                        </StyledMenuItem>
                    ))
                )}
            </Select>
        </FormControl>
    );
};

export default Filter;